import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { Collapse, IconButton, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { getIn } from "formik";
import { defineMessages, useIntl } from "react-intl";
import clsx from "clsx";
import { LiteralBase, getLiteralBaseProps, baseEditableOptions, typographyVariants } from "./TextLiteral";
import { useFormattedParts } from "./NumberLiteral";
import { identityRecordOfFieldEditorOptions } from "@ploy-ui/form-fields";
import { removeNegativeZero } from "../../utils";
const messages = defineMessages({
    toggle: {
        id: "template-form.expandable-literal.toggle",
        description: "Aria label for the button that expands/collapses the details of a literal field",
        defaultMessage: "Show details"
    }
});
const useStyles = makeStyles(theme => ({
    root: {
        width: "100%"
    },
    header: {
        display: "flex",
        alignItems: "center",
        width: "100%"
    },
    toggle: {
        padding: 2,
        marginLeft: theme.spacing(0.5),
        transition: theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest
        })
    },
    toggleExpanded: {
        transform: "rotate(180deg)"
    },
    toggleSpacer: {
        width: 28 + theme.spacing(0.5),
        flexShrink: 0
    },
    details: {
        paddingLeft: theme.spacing(2)
    }
}), { name: "DployExpandableLiteral" });
function getDetailPath(fieldName, key) {
    if (key.includes("."))
        return key;
    const match = /^(.*\.)?[^.]+(\.value)$/.exec(fieldName);
    return match ? `${match[1] ?? ""}${key}${match[2]}` : key;
}
function toDetailItems(items) {
    if (!Array.isArray(items))
        return [];
    return items
        .map((item) => ({
        key: item?.key ?? item?.Key,
        value: item?.value ?? item?.Value
    }))
        .filter(item => typeof item.key === "string" && item.key.trim() !== "");
}
const formatOptions = (format) => format === "currency" ? { format: "currency_input" } : {};
const toNumber = (value) => removeNegativeZero(value === "" || value == null ? NaN : Number(value));
const isEmpty = (value) => value == null || (typeof value === "string" && value.trim() === "");
function DetailRow(props) {
    const { baseProps, label, value, format, emptyEqualsZero, formatString } = props;
    const numericValue = toNumber(value);
    const isText = Number.isNaN(numericValue) && !isEmpty(value);
    const { prefix = props.prefix, str, suffix = props.suffix } = useFormattedParts(numericValue, formatOptions(format), emptyEqualsZero, formatString);
    return (_jsx(LiteralBase, { ...baseProps, label: label, error: false, helperText: undefined, literalVariant: "plain", margin: "dense", prefix: isText ? undefined : prefix, suffix: isText ? undefined : suffix, children: isText ? value : str }));
}
function ExpandableLiteral({ format, emptyEqualsZero, formatString, ...rest }) {
    const classes = useStyles();
    const intl = useIntl();
    const [expanded, setExpanded] = useState(false);
    const { suffix: suffixValue = undefined, prefix: prefixValue = undefined } = rest;
    const { prefix = prefixValue, str, suffix = suffixValue } = useFormattedParts(toNumber(rest.field.value), formatOptions(format), emptyEqualsZero, formatString);
    const details = toDetailItems(rest.items)
        .map(item => ({
        ...item,
        detailValue: getIn(rest.form.values, getDetailPath(rest.field.name, item.key))
    }))
        .filter(item => !isEmpty(item.detailValue));
    const baseProps = getLiteralBaseProps(rest);
    const header = (_jsx(LiteralBase, { ...baseProps, prefix: prefix, suffix: suffix, children: str }));
    if (details.length === 0)
        return header;
    return (_jsxs("div", { className: classes.root, children: [_jsxs("div", { className: classes.header, children: [header, _jsx(IconButton, { size: "small", className: clsx(classes.toggle, {
                            [classes.toggleExpanded]: expanded
                        }), onClick: () => setExpanded(e => !e), "aria-expanded": expanded, "aria-label": intl.formatMessage(messages.toggle), children: _jsx(ExpandMoreIcon, { fontSize: "small" }) })] }), _jsx(Collapse, { in: expanded, unmountOnExit: true, children: _jsx("div", { className: classes.details, children: details.map(item => (_jsxs("div", { className: classes.header, children: [_jsx(DetailRow, { baseProps: baseProps, label: item.value, value: item.detailValue, format: format, emptyEqualsZero: emptyEqualsZero, formatString: formatString, prefix: prefixValue, suffix: suffixValue }), _jsx("span", { className: classes.toggleSpacer })] }, item.key))) }) })] }));
}
export const ExpandableCurrencyLiteral = (props) => (_jsx(ExpandableLiteral, { ...props, format: "currency" }));
export const ExpandableNumberLiteral = (props) => (_jsx(ExpandableLiteral, { ...props, format: "number" }));
const expandableEditableOptions = {
    ...baseEditableOptions,
    typographyVariant: typographyVariants,
    prefix: true,
    suffix: true
};
export const EditorExpandableLiterals = identityRecordOfFieldEditorOptions({
    ExpandableCurrencyLiteral: {
        editableOptions: expandableEditableOptions
    },
    ExpandableNumberLiteral: {
        editableOptions: expandableEditableOptions
    }
});
//# sourceMappingURL=ExpandableLiteral.js.map